import { Box, Typography } from '@mui/material';
import { NewCard, UserOwnedNftCardType } from '../../../../types/types';

const rarities = ['common', 'rare', 'epic', 'legendary', 'relic'] as const;
type Rarity = (typeof rarities)[number];

// Arena palette — the rarity accents mirror the in-game .ccard / rarityHex()
// used on the collection cards, so a count reads the same colour as its cards.
const C = {
  text: '#e8f4fb',
  dim: '#8ea3b3',
  mono: 'ui-monospace, "SF Mono", Menlo, Consolas, monospace'
};
const rarityHex: Record<Rarity, string> = {
  common: '#8b919c', // grey
  rare: '#a06fff', // purple
  epic: '#42e6a4', // green
  legendary: '#ff5d5d', // red
  relic: '#ffcf40' // gold / yellow
};

interface NftItem {
  amount: number;
  nft: {
    rarity: string;
  };
}

type TotalNFTCountProps = {
  listOfAllUserOwnedNft?: UserOwnedNftCardType[] | null;
  listOfNewNft?: NewCard[] | null;
  // Average active-listing price (INC) per card id, for the total-value tile.
  avgPrices?: Record<number, number> | null;
  // Current INC->USD rate (USD per 1 INC), for the $ estimate beside the total.
  incUsd?: number | null;
};

const emptyCounts = (): Record<Rarity, number> => ({
  common: 0,
  rare: 0,
  epic: 0,
  legendary: 0,
  relic: 0
});

// One stat tile — big colour-coded number over a mono kicker label.
const StatCard = ({
  label,
  value,
  color
}: {
  label: string;
  value: number;
  color: string;
}) => (
  <Box
    sx={{
      borderRadius: '14px',
      p: { xs: 1.75, md: 2 },
      display: 'flex',
      flexDirection: 'column',
      alignItems: 'center',
      justifyContent: 'center',
      gap: 0.75,
      border: '1px solid rgba(56, 232, 255, 0.18)',
      background:
        'linear-gradient(180deg, rgba(11, 17, 25, 0.74), rgba(7, 11, 17, 0.82))',
      boxShadow: '0 14px 30px -24px rgba(0, 0, 0, 0.9)'
    }}
  >
    <Typography
      sx={{
        fontWeight: 900,
        fontSize: { xs: 28, md: 34 },
        lineHeight: 1,
        color,
        fontVariantNumeric: 'tabular-nums',
        textShadow: `0 0 26px ${color}55`
      }}
    >
      {value.toLocaleString('en-AU')}
    </Typography>
    <Typography
      sx={{
        font: `800 10px ${C.mono}`,
        letterSpacing: '0.16em',
        textTransform: 'uppercase',
        color: C.dim,
        textAlign: 'center'
      }}
    >
      {label}
    </Typography>
  </Box>
);

const TotalNFTCount = ({
  listOfAllUserOwnedNft = null,
  listOfNewNft = null,
  avgPrices = null,
  incUsd = 0
}: TotalNFTCountProps) => {
  const countRaritiesNFTCollection = (
    nfts: NftItem[]
  ): Record<Rarity, number> => {
    const counts = emptyCounts();
    for (const item of nfts) {
      const rarity = item.nft.rarity.toLowerCase() as Rarity;
      if (rarities.includes(rarity)) {
        counts[rarity] += item.amount;
      }
    }
    return counts;
  };

  const countCardRaritiesNewCards = (
    cards: NewCard[]
  ): Record<Rarity, number> => {
    const counts = emptyCounts();
    for (const card of cards) {
      const rarity = card.rarity.toLowerCase() as Rarity;
      if (rarities.includes(rarity)) {
        counts[rarity]++;
      }
    }
    return counts;
  };

  const counts =
    listOfAllUserOwnedNft !== null
      ? countRaritiesNFTCollection(listOfAllUserOwnedNft)
      : listOfNewNft !== null
      ? countCardRaritiesNewCards(listOfNewNft)
      : emptyCounts();

  const total =
    listOfAllUserOwnedNft !== null
      ? listOfAllUserOwnedNft.reduce((sum, item) => sum + item.amount, 0)
      : listOfNewNft !== null
      ? listOfNewNft.length
      : 0;

  // Total market value = sum of (avg active-listing price x owned quantity), INC.
  const totalValue =
    listOfAllUserOwnedNft !== null && avgPrices
      ? listOfAllUserOwnedNft.reduce(
          (sum, item) =>
            sum +
            item.amount *
              (avgPrices[Number((item.nft as { id?: number })?.id)] ?? 0),
          0
        )
      : 0;

  // Same conversion the marketplace cards use: INC amount x USD-per-INC rate.
  const totalValueUsd = totalValue * (Number(incUsd) || 0);

  // The six count tiles divide the grid evenly (6 / 3 / 2 cols) so nothing is
  // orphaned onto a lonely row. The estimated value is a headline metric, so it
  // gets its own full-width banner above instead of a 7th tile that wraps.
  const countStats: { label: string; value: number; color: string }[] = [
    { label: 'Total Collectibles', value: total, color: C.text },
    { label: 'Relics', value: counts.relic, color: rarityHex.relic },
    { label: 'Legendaries', value: counts.legendary, color: rarityHex.legendary },
    { label: 'Epics', value: counts.epic, color: rarityHex.epic },
    { label: 'Rares', value: counts.rare, color: rarityHex.rare },
    { label: 'Commons', value: counts.common, color: rarityHex.common }
  ];

  return (
    <Box
      sx={{
        display: 'flex',
        flexDirection: 'column',
        gap: { xs: 1.25, md: 1.75 }
      }}
    >
      {listOfAllUserOwnedNft !== null && (
        <Box
          sx={{
            borderRadius: '14px',
            p: { xs: 1.75, md: 2.25 },
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            gap: 2,
            flexWrap: 'wrap',
            border: '1px solid rgba(56, 232, 255, 0.28)',
            background:
              'linear-gradient(180deg, rgba(56, 232, 255, 0.08), rgba(7, 11, 17, 0.82))',
            boxShadow: '0 14px 30px -24px rgba(0, 0, 0, 0.9)'
          }}
        >
          <Box>
            <Typography
              sx={{
                font: `800 10px ${C.mono}`,
                letterSpacing: '0.18em',
                textTransform: 'uppercase',
                color: C.dim
              }}
            >
              Estimated Total Value
            </Typography>
            <Typography
              sx={{
                font: `600 11px ${C.mono}`,
                color: C.dim,
                opacity: 0.75,
                mt: 0.5
              }}
            >
              Based on average marketplace listings
            </Typography>
          </Box>
          <Box
            sx={{
              display: 'flex',
              alignItems: 'baseline',
              justifyContent: 'flex-end',
              flexWrap: 'wrap',
              columnGap: 1,
              rowGap: 0.25
            }}
          >
            <Box sx={{ display: 'flex', alignItems: 'baseline', gap: 0.6 }}>
              <Typography
                sx={{
                  fontWeight: 900,
                  fontSize: { xs: 28, md: 36 },
                  lineHeight: 1,
                  color: '#38e8ff',
                  fontVariantNumeric: 'tabular-nums',
                  textShadow: '0 0 26px rgba(56, 232, 255, 0.33)'
                }}
              >
                {Math.round(totalValue).toLocaleString('en-AU')}
              </Typography>
              <Typography
                sx={{
                  font: `800 13px ${C.mono}`,
                  letterSpacing: '0.12em',
                  color: C.dim
                }}
              >
                INC
              </Typography>
            </Box>
            {incUsd && totalValueUsd > 0 ? (
              <Box sx={{ display: 'flex', alignItems: 'baseline', gap: 0.6 }}>
                <Typography
                  sx={{
                    fontSize: { xs: 22, md: 28 },
                    lineHeight: 1,
                    color: C.dim
                  }}
                >
                  /
                </Typography>
                <Typography
                  sx={{
                    fontWeight: 900,
                    fontSize: { xs: 22, md: 28 },
                    lineHeight: 1,
                    color: C.text,
                    fontVariantNumeric: 'tabular-nums'
                  }}
                >
                  {'$'}
                  {totalValueUsd.toLocaleString('en-AU', {
                    minimumFractionDigits: 2,
                    maximumFractionDigits: 2
                  })}
                </Typography>
                <Typography
                  sx={{
                    font: `800 13px ${C.mono}`,
                    letterSpacing: '0.12em',
                    color: C.dim
                  }}
                >
                  USD
                </Typography>
              </Box>
            ) : null}
          </Box>
        </Box>
      )}

      <Box
        sx={{
          display: 'grid',
          gap: { xs: 1.25, md: 1.75 },
          gridTemplateColumns: {
            xs: 'repeat(2, 1fr)',
            sm: 'repeat(3, 1fr)',
            lg: 'repeat(6, 1fr)'
          }
        }}
      >
        {countStats.map((s) => (
          <StatCard key={s.label} {...s} />
        ))}
      </Box>
    </Box>
  );
};

export default TotalNFTCount;
